import type { StackView, Tone } from '../types';
import { CANVAS, GEO, toneColor, toneFill } from '../theme';

import { Caption, FONT, LAYOUT, fmt, frameProps, valueWidth } from './shared';

const PAD = GEO.padding;
const V_CELL_W = 104;
const V_CELL_H = 40;
const H_CELL_W = GEO.cell;
const H_CELL_H = 48;
const GAP = 4;
const END_BAND = 20;
/** Breathing room reserved on each side of a cell's text before the box grows. */
const CELL_TEXT_PAD = 16;

export function StackRenderer({ view }: { view: StackView }) {
  const horizontal = view.orientation === 'horizontal';
  const endLabel = view.endLabel ?? (horizontal ? 'front' : 'top');
  const n = view.values.length;
  const title =
    view.caption ?? (n === 0 ? `Empty ${horizontal ? 'queue' : 'stack'}` : `${horizontal ? 'Queue' : 'Stack'} of ${n} values`);

  // `marks` is a CellMark, whose `label` the array and grid renderers both draw.
  // Dropping it here silently lost annotations like "resolves i=1" on the cell
  // being popped, which is the most useful thing to say in a stack lesson.
  const marks = new Map<number, { tone: Tone; label?: string }>();
  for (const mark of view.marks ?? []) {
    if (Number.isInteger(mark.index) && mark.index >= 0 && mark.index < n) {
      marks.set(mark.index, { tone: mark.tone, label: mark.label });
    }
  }
  const hasMarkLabel = [...marks.values()].some((m) => m.label);

  const captionY = view.caption ? PAD + 8 : 0;
  const bodyTop = (view.caption ? PAD + LAYOUT.captionBand : PAD) + END_BAND;

  if (n === 0) {
    const w = PAD * 2 + (horizontal ? H_CELL_W : V_CELL_W);
    const h = bodyTop + (horizontal ? H_CELL_H : V_CELL_H) + PAD;
    return (
      <svg {...frameProps(w, h, view.caption)}>
        <title>{title}</title>
        {view.caption ? <Caption text={view.caption} x={PAD} y={captionY} /> : null}
        <rect
          x={PAD}
          y={bodyTop}
          width={horizontal ? H_CELL_W : V_CELL_W}
          height={horizontal ? H_CELL_H : V_CELL_H}
          rx={8}
          fill="none"
          stroke={CANVAS.cellBorder}
          strokeDasharray="4 4"
          strokeWidth={1.5}
          aria-hidden="true"
        />
        <text
          x={PAD + (horizontal ? H_CELL_W : V_CELL_W) / 2}
          y={bodyTop + (horizontal ? H_CELL_H : V_CELL_H) / 2}
          fill={CANVAS.textFaint}
          fontSize={FONT.label}
          textAnchor="middle"
          dominantBaseline="central"
          aria-hidden="true"
        >
          empty
        </text>
      </svg>
    );
  }

  // A recursion-frame stack ("factorial(4)", "subsets(i=2, [A,B]) → record {A,B}")
  // can carry values far longer than a plain int/char stack — grow the cell to
  // fit its own longest value rather than letting the text spill past a fixed
  // box (which the outer <svg>'s viewBox would then clip at the frame edge).
  const longestValueW = view.values.length
    ? Math.max(...view.values.map((v) => valueWidth(fmt(v))))
    : 0;
  const cellW = Math.max(horizontal ? H_CELL_W : V_CELL_W, longestValueW + CELL_TEXT_PAD * 2);
  const cellH = horizontal ? H_CELL_H : V_CELL_H;

  // Vertical stacks read bottom-up: the LAST array element is the top of the
  // stack, so draw order is reversed against the index.
  const cellX = (i: number) => (horizontal ? PAD + i * (cellW + GAP) : PAD);
  const cellY = (i: number) => (horizontal ? bodyTop : bodyTop + (n - 1 - i) * (cellH + GAP));

  // Labels sit to the right of a vertical stack and below a horizontal one, so
  // each orientation has to reserve room on a different axis.
  const labelRoom = hasMarkLabel ? 96 : 0;
  const width = PAD * 2 + (horizontal ? n * cellW + (n - 1) * GAP : cellW + labelRoom);
  const height =
    bodyTop + (horizontal ? cellH + (hasMarkLabel ? LAYOUT.labelBand : 0) : n * cellH + (n - 1) * GAP) + PAD;

  return (
    <svg {...frameProps(width, height, view.caption)}>
      <title>{title}</title>
      {view.caption ? <Caption text={view.caption} x={PAD} y={captionY} /> : null}

      <text
        x={PAD + cellW / 2}
        y={bodyTop - END_BAND / 2}
        fill={CANVAS.textDim}
        fontSize={FONT.label}
        textAnchor="middle"
        dominantBaseline="central"
        aria-hidden="true"
      >
        {endLabel}
      </text>

      {view.values.map((value, i) => {
        const mark = marks.get(i);
        const tone = mark?.tone;
        return (
          <g key={i}>
            <rect
              x={cellX(i)}
              y={cellY(i)}
              width={cellW}
              height={cellH}
              rx={8}
              fill={tone ? toneFill(tone) : CANVAS.cell}
              stroke={tone ? toneColor(tone) : CANVAS.cellBorder}
              strokeWidth={1.5}
            />
            <text
              x={cellX(i) + cellW / 2}
              y={cellY(i) + cellH / 2}
              fill={CANVAS.text}
              fontSize={FONT.value}
              textAnchor="middle"
              dominantBaseline="central"
            >
              {fmt(value)}
            </text>
            {mark?.label ? (
              <text
                x={horizontal ? cellX(i) + cellW / 2 : cellX(i) + cellW + 8}
                y={horizontal ? cellY(i) + cellH + LAYOUT.labelBand / 2 : cellY(i) + cellH / 2}
                fill={toneColor(tone)}
                fontSize={FONT.label}
                textAnchor={horizontal ? 'middle' : 'start'}
                dominantBaseline="central"
              >
                {mark.label}
              </text>
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

export default StackRenderer;
